import {
  getCookie,
  setCookie,
  delCookie,
  setLS,
  getLS,
} from 'hobbit-base/modules/utils';
/**
 * 用户身份认证模块(请使用this.$user)
 * 1、检测用户登录态
 * 2、跳转登录页
 * 3、退出登录
 */
export default class User {
  constructor($config, $fetch, $ctx) {
    this.$config = $config;
    this.$fetch = $fetch;
    this.$ctx = $ctx;
    this.tokenCheckTime = new Date(0);

    // URL参数学生ID初始化
    const studentid = this.$ctx.query.studentid;
    if (studentid) {
      setLS(this.$config.user.studentIdKey, studentid);
    }
  }

  /**
   * 获取或设置鉴权Token（注意，老网站cookie都是写在完整域名下，不跨主域的。需要兼容老网站。）
   */
  vkToken(v, expires) {
    if (v === null) {
      delCookie(this.$config.user.tokenKey, '/');
      return null;
    }
    if (v) {
      setCookie(this.$config.user.tokenKey, v, expires, '/');
      this.tokenCheckTime = new Date().getTime();
      return v;
    }
    return getCookie(this.$config.user.tokenKey);
  }

  /**
   * 持久化用户ID
   */
  vkUserId(v) {
    if (v) {
      setLS(this.$config.user.userIdKey, v);
      return v;
    }

    return getLS(this.$config.user.userIdKey);
  }

  /**
   * 持久化学生ID
   */
  vkStudentId(v) {
    if (v) {
      setLS(this.$config.user.studentIdKey, v);
      return v;
    }

    return getLS(this.$config.user.studentIdKey);
  }

  /**
   * 获取当前学生Level
   */
  async vkStudentLevel() {
    const studentList = await this.getStudentInfo();
    const currentStudentId = this.vkStudentId();
    if (!studentList) {
      return {
        currentLevel: '',
        nextBookLevel: '',
      };
    }
    const currentInfo = studentList.find(v => v.studentId === +currentStudentId);
    if (!currentInfo) {
      return {
        currentLevel: '',
        nextBookLevel: '',
      };
    }
    return {
      currentLevel: `LEVEL_${currentInfo.level.split(' ')[1]}`, // 当前level
      nextBookLevel: currentInfo.nextBookLevel, // 下级level ，约课相关需要使用此level，展示需要使用level
    };
  }

  /**
   * 获取所有孩子信息
   */
  async getStudentInfo(preventDefaultAuthCheck = true) {
    const resp = await this.$fetch.get({
      url: '/rest/gw/api/class/student/info',
      timeout: 1e4,
      preventDefaultAuthCheck,
    });
    if (resp.errorCode) {
      return null;
    }
    return resp.data?.data || null;
  }

  /**
   * 获取用户tags集合
   */
  // async getUserTags(args = {}, autoRedirect = false, ignoreCache) {
  //   if (await this.isLogin(autoRedirect)) {
  //     const resp = await this.$fetch.get({
  //       url: '/rest/gw/tag/api/rpc/tag/listByTargetId',
  //       timeout: 1e4,
  //       params: {
  //         business: 'lingobus', // TODO...
  //         targetType: 'user',
  //         targetId: this.vkUserId(),
  //         ...args,
  //       },
  //       preventDefaultAuthCheck: true,
  //       cache: ignoreCache ? undefined : {
  //         online: 600 * 1e3,
  //         offline: 3600 * 1e3,
  //       },
  //     });
  //     if (resp.errorCode) {
  //       return null;
  //     }
  //     return resp.data?.data || null;
  //   }
  //   return null;
  // }

  /**
   * 获取家庭信息
   */
  async getFamilyDetail(autoRedirect = false, ignoreCache) {
    if (await this.isLogin(autoRedirect)) {
      const resp = await this.$fetch.get({
        url: '/rest/gw/api/intern/family/getDetail',
        timeout: 1e4,
        preventDefaultAuthCheck: true,
        cache: ignoreCache ? undefined : {
          online: 600 * 1e3,
          offline: 3600 * 1e3,
        },
      });
      if (resp.errorCode) {
        return null;
      }
      return resp.data?.data || null;
    }
    return null;
  }

  /**
   * 更新家庭信息缓存
   */
  updateFamilyDetailCache() {
    this.$fetch.removeCache('/rest/gw/api/intern/family/getDetail');
  }

  /**
   * 获取用户Tag数据
   */
  async getTag(tagId, ignoreCache) {
    const familyDetail = await this.getFamilyDetail(false, ignoreCache);
    if (!familyDetail) return null;
    const tags = familyDetail?.parent?.parentTags;
    if (!tags || !tags.length) return null;
    return tags.find(i => i.id === tagId);
  }

  /**
   * 是否已登录
   */
  async isLogin(autoRedirect = false, backurl) {
    const token = this.vkToken();
    if (token) {
      const nowTime = new Date().getTime();
      if (nowTime - this.tokenCheckTime < this.$config.user.tokenRecheckSeconds * 1e3) {
        // token免检期内
        return true;
      }

      // 距离上次token检测时间已经超过阀值，需要重新检查
      const passed = await this.checkToken(token);
      if (passed) {
        return true;
      }

      // 删除无效的token和登录id
      this.vkToken(null);
    }

    // 无token，判断是否要自动跳转登录页
    if (autoRedirect) {
      this.gotoLogin(backurl);
    }

    return false;
  }

  /**
   * 跳转登录页
   */
  // eslint-disable-next-line class-methods-use-this
  gotoLogin(backurl, ani) {
    if (!backurl) {
      backurl = window.location.href;
    }
    const loginUrl = `/signin/?backurl=${encodeURIComponent(backurl)}`;
    window.$hobbit.$app.pageTo(loginUrl, ani);
  }

  /**
   * 检查token是否有效
   */
  async checkToken(tk) {
    if (!tk) {
      return false;
    }

    const resp = await this.$fetch.get({
      url: '/rest/gw/api/intern/user/checkLoginStatus',
      timeout: 3000,
    });

    if (!resp.errorCode && resp.data) {
      this.tokenCheckTime = new Date().getTime();
      return true;
    }
    return false;
  }

  /**
   * 退出登录态
   */
  async logout(backTo = '/') {
    // 删除本地的登录态信息
    this.vkToken(null);
    // 请求服务器端删除token
    await this.$fetch.post({
      url: '/rest/gw/api/intern/user/logout',
      timeout: 3000,
    });
    window.$hobbit.$app.pageTo(backTo).then(code => {
      if (code === 16) {
        window.$hobbit.$app.pageReload();
      }
    });
  }

  /**
   * 获取用户邀请码
   */
  async getReferralCode(autoRedirect = false) {
    if (await this.isLogin(autoRedirect)) {
      const resp = await this.$fetch.get({
        url: '/rest/gw/api/intern/parent/getReferral',
        timeout: 1e4,
        preventDefaultAuthCheck: true,
        cache: {
          online: 24 * 3600 * 1e3,
          offline: 240 * 3600 * 1e3,
        },
      });
      if (resp.errorCode) {
        return null;
      }
      return resp.data?.data || null;
    }
    return null;
  }
}
